<template>
    <div class="flex-form">
        <section class="flex-form__section">
            <h2 class="flex-form__title">
                {{ headingText }}
            </h2>

            <div class="flex-form__fieldset">
                <label class="flex-form__field flex-form__field--expand" :class="{ [STATE_CLASSES.INVALID]: touched && !isValidLabel }">
                    <div class="flex-form__label">
                        Title
                    </div>

                    <input
                        v-model="flexEventNew.label"
                        class="flex-form__input"
                        type="text"
                    >
                </label>
            </div>
        </section>

        <section class="flex-form__section flex-form__section--left">
            <h2 class="flex-form__section-title">
                Trigger

                <tooltip>
                    This dictates when the event will fire.
                </tooltip>
            </h2>

            <div class="flex-form__fieldset">
                <label class="flex-form__field" :class="{ [STATE_CLASSES.INVALID]: touched && !isValidTrigger }">
                    <div class="flex-form__label">
                        Type
                    </div>

                    <dropdown
                        v-model="flexEventNew.trigger"
                        class="flex-form__dropdown"
                        placeholder="Select One"
                        :options="triggerOptions"
                    />
                </label>

                <component
                    :is="triggerDataComponent"
                    v-if="triggerDataComponent"
                    :data="flexEventNew.data"
                    :touched="touched"
                    @input="onDataInput"
                    @valid="isValid => isValidTriggerData = isValid"
                />
            </div>
        </section>

        <section v-if="flexEventNew.trigger" class="flex-form__section flex-form__section--right">
            <h2 class="flex-form__section-title">
                Action

                <tooltip>
                    This dictates what will happen when the event is fired.
                </tooltip>
            </h2>

            <div class="flex-form__fieldset">
                <label class="flex-form__field" :class="{ [STATE_CLASSES.INVALID]: touched && !isValidAction }">
                    <div class="flex-form__label">
                        Type
                    </div>

                    <dropdown
                        v-model="flexEventNew.action"
                        class="flex-form__dropdown"
                        placeholder="Select One"
                        :options="actionOptions"
                    />
                </label>

                <component
                    :is="actionDataComponent"
                    v-if="actionDataComponent"
                    :data="flexEventNew.data"
                    :touched="touched"
                    @input="onDataInput"
                    @valid="isValid => isValidActionData = isValid"
                />
            </div>
        </section>

        <section v-if="flexEventNew.action" class="flex-form__section">
            <h2 class="flex-form__section-title">
                Conditions

                <tooltip>
                    The conditions that must be present in order for this event to fire.
                </tooltip>
            </h2>

            <!--  temporarily hard-coded index, until we fully support "and" + "or" -->
            <!-- see FlexEvent in data-models.js for more info -->
            <div v-for="(condition, index) in flexEventNew.conditions[0]" :key="condition.id" class="flex-form__fieldset condition">
                <span class="flex-form__inline-text condition__label">
                    {{ index === 0 ? 'Only run if the' : 'and if the' }}
                </span>

                <dropdown
                    :value="condition.target"
                    class="flex-form__dropdown flex-form__dropdown--inline"
                    :class="{ [STATE_CLASSES.INVALID]: touched && !condition.target }"
                    theme="underline"
                    placeholder="Select One"
                    :options="conditionTargetOptions"
                    @input="value => onConditionInput(condition, 'target', value)"
                />

                <component
                    :is="getConditionTargetComponent(condition.target)"
                    v-if="getConditionTargetComponent(condition.target)"
                    :data="{ targetProperty: condition.targetProperty }"
                    :touched="touched"
                    @input="obj => onConditionInput(condition, 'targetProperty', obj.targetProperty)"
                />

                <dropdown
                    v-if="condition.target"
                    :value="condition.operator"
                    class="flex-form__dropdown flex-form__dropdown--inline"
                    :class="{ [STATE_CLASSES.INVALID]: touched && !condition.operator }"
                    theme="underline"
                    placeholder="Select One"
                    :options="conditionOperatorOptions"
                    @input="value => onConditionInput(condition, 'operator', value)"
                />

                <component
                    :is="getConditionOperatorComponent(condition.operator)"
                    v-if="getConditionOperatorComponent(condition.operator)"
                    :data="{ targetValue: condition.targetValue }"
                    :touched="touched"
                    @input="obj => onConditionInput(condition, 'targetValue', obj.targetValue)"
                />

                <button
                    type="button"
                    class="condition__delete"
                    @click="onDeleteCondition(index)"
                >
                    <font-awesome-icon :icon="['fal', 'times']" />
                </button>
            </div>

            <button class="flex-form__add" @click="onAddCondition">
                <font-awesome-icon :icon="['fal', 'plus']" />
            </button>
        </section>

        <footer class="flex-form__footer">
            <button class="flex-form__button" type="button" @click="onSave">
                {{ buttonText }}
            </button>

            <button class="flex-form__button flex-form__button--alt" type="button" @click="onCancel">
                Clear
            </button>
        </footer>
    </div>
</template>

<script>
import { mapState } from 'vuex';

import { FlexEventGroup, FlexEvent, FlexCondition } from '@/data/data-models';
import triggers, { triggerNames } from '@/data/events/triggers';
import actions, { actionNames } from '@/data/events/actions';
import conditionTargets from '@/data/events/condition-targets';
import conditionOperators, { conditionOperatorNames } from '@/data/events/condition-operators';
import getTriggerMapping from '@/data/events/trigger-mappings';
import getConditionOperatorMapping from '@/data/events/condition-mappings';
import { STATE_CLASSES } from '@/data/constants/classes';

const CUSTOM_ATTR_NAME = 'flex-event-group';

export default {
    name: 'flex-event-editor',

    components: {
        ActionFireEvent: () => import('./actions/action-fire-event'),
        ActionHide: () => import('./actions/action-hide'),
        ActionPause: () => import('./actions/action-pause'),
        ActionRunAnimation: () => import('./actions/action-run-animation'),
        ActionRunJavascript: () => import('./actions/action-run-javascript'),
        ActionScrollIntoFocus: () => import('./actions/action-scroll-into-focus'),
        ActionScrollToTop: () => import('./actions/action-scroll-to-top'),
        ActionShow: () => import('./actions/action-show'),
        ActionValidateForm: () => import('./actions/action-validate-form'),
        ActionToggleLayeredContainer: () => import('./actions/action-toggle-layered-container'),
        ActionToggle: () => import('./actions/action-toggle'),
        ActionUrlRedirect: () => import('./actions/action-url-redirect'),
        ConditionOperatorEquals: () => import('./conditions/condition-operator-equals'),
        ConditionOperatorGreaterLess: () => import('./conditions/condition-operator-greater-less'),
        ConditionOperatorIncludes: () => import('./conditions/condition-operator-includes'),
        ConditionTargetCookie: () => import('./conditions/condition-target-cookie'),
        ConditionTargetForm: () => import('./conditions/condition-target-form'),
        ConditionTargetFormResponse: () => import('./conditions/condition-target-form-response'),
        ConditionCollectionData: () => import('./conditions/condition-collection-data'),
        TriggerClick: () => import('./triggers/trigger-click'),
        TriggerFlexEvent: () => import('./triggers/trigger-flex-event'),
        TriggerFormSubmit: () => import('./triggers/trigger-form-submit'),
        TriggerView: () => import('./triggers/trigger-view'),
        TriggerCollectionLoad: () => import('./triggers/trigger-collection-load'),
    },

    props: {
        flexEvent: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            flexEventNew: new FlexEvent(),
            isValidTriggerData: true,
            isValidActionData: true,
            touched: false,
            STATE_CLASSES,
        };
    },

    computed: {
        ...mapState({
            pageData: state => state.page.pageData,
            selectedElements: state => state.page.selectedElements,
        }),

        triggerDataComponent() {
            const triggerDef = triggers.get(this.flexEventNew.trigger);

            return triggerDef ? triggerDef.component : '';
        },

        actionDataComponent() {
            const actionDef = actions.get(this.flexEventNew.action);

            return actionDef ? actionDef.component : '';
        },

        triggerOptions() {
            if (!this.selectedElements.length) return [];

            const options = [];
            const { elementType } = this.selectedElements[0];

            triggers.forEach((trigger) => {
                if (getTriggerMapping(elementType).includes(trigger.name)) {
                    options.push({
                        text: trigger.label,
                        value: trigger.name,
                    });
                }
            });

            return options;
        },

        actionOptions() {
            const options = [];

            actions.forEach((a) => {
                options.push({
                    text: a.label,
                    value: a.name,
                });
            });

            return options;
        },

        conditionTargetOptions() {
            const options = [{ text: 'Select One', value: '' }];

            conditionTargets
                .forEach((t) => {
                    // don't add the condition target, if its only supposed to display for certain triggers
                    // ... and the current trigger doesn't match. See FlexConditionTarget in data-models.js
                    if (t.triggers.length && !t.triggers.includes(this.flexEventNew.trigger)) {
                        return;
                    }

                    options.push({
                        text: t.label,
                        value: t.name,
                    });
                });

            return options;
        },

        conditionOperatorOptions() {
            const options = [{ text: 'Select One', value: '' }];

            conditionOperators.forEach((a) => {
                if (getConditionOperatorMapping(this.flexEventNew.trigger).includes(a.name)) {
                    options.push({
                        text: a.label,
                        value: a.name,
                    });
                }
            });

            return options;
        },

        isEditingMode() {
            return !!this.flexEventNew.id;
        },

        isEventAction() {
            return this.flexEventNew.action === actionNames.FIRE_EVENT;
        },

        headingText() {
            return this.isEditingMode ? 'Edit Event' : 'Add Event';
        },

        buttonText() {
            return this.isEditingMode ? 'Save' : 'Add';
        },

        // form validation - begin
        isValidLabel() {
            return !!this.flexEventNew.label;
        },

        isValidTrigger() {
            return !!this.flexEventNew.trigger;
        },

        isValidAction() {
            return this.flexEventNew.trigger === triggerNames.FLEX_EVENT || !!this.flexEventNew.action;
        },

        isValidData() {
            return this.isValidTriggerData && this.isValidActionData;
        },

        isValidCondition() {
            return this.hasValidConditionTargets()
                && this.hasValidConditionOperators()
                && this.hasValidConditionProperties()
                && this.hasValidConditionValues();
        },

        isValidForm() {
            return this.isValidLabel
                && this.isValidTrigger
                && this.isValidAction
                && this.isValidData
                && this.isValidCondition;
        },
        // form validation - end
    },

    watch: {
        flexEvent() {
            if (!this.flexEvent) {
                this.reset();
                return;
            }

            this.flexEventNew = new FlexEvent({ ...this.flexEvent });
            this.touched = false;
        },
    },

    methods: {
        onDataInput(data) {
            this.flexEventNew.data = {
                ...this.flexEventNew.data,
                ...data,
            };
        },

        onConditionInput(condition, prop, value) {
            condition[prop] = value;

            switch (true) {
                case prop === 'operator' && value === conditionOperatorNames.EXISTS:
                case prop === 'operator' && value === conditionOperatorNames.NOT_EXISTS:
                    // the EXISTS and NOT_EXISTS operators do not have/need target values
                    condition.targetValue = '';
                    break;

                default:
                    // do nothing
            }
        },

        onAddCondition() {
            // temporarily hard-coded index, until we fully support "and" + "or"
            // see FlexEvent in data-models.js for more info
            this.flexEventNew.conditions[0].push(new FlexCondition());
        },

        onDeleteCondition(index) {
            // temporarily hard-coded index, until we fully support "and" + "or"
            // see FlexEvent in data-models.js for more info
            this.flexEventNew.conditions[0].splice(index, 1);
        },

        onSave() {
            this.touched = true;

            if (!this.isValidForm) return;

            const flexEventGroup = this.selectedElements[0].getElementData(CUSTOM_ATTR_NAME) || new FlexEventGroup();
            const flexEvents = flexEventGroup[this.flexEventNew.trigger];

            const existingEvent = flexEvents.find(fe => fe.id === this.flexEventNew.id);
            const updatedEvent = this.flexEventNew;

            if (this.isEditingMode && existingEvent) {
                // it's possible to be in editing mode, and NOT have an existing event
                // ... if the user deletes the event from the list in the left panel, while editing it in the right panel
                Object.assign(existingEvent, updatedEvent);
            } else {
                flexEvents.push(updatedEvent);
            }

            this.selectedElements[0].setElementData(CUSTOM_ATTR_NAME, flexEventGroup);
            this.$emit('update', updatedEvent);
            this.reset();
        },

        onCancel() {
            this.reset();
            this.$emit('cancel');
        },

        getConditionTargetComponent(target) {
            const targetDef = conditionTargets.get(target);

            return targetDef ? targetDef.component : '';
        },

        getConditionOperatorComponent(operator) {
            const operatorDef = conditionOperators.get(operator);

            return operatorDef ? operatorDef.component : '';
        },

        hasValidConditionTargets() {
            // temporarily hard-coded index, until we fully support "and" + "or"
            // see FlexEvent in data-models.js for more info
            return this.flexEventNew.conditions[0].every(c => !!c.target);
        },

        hasValidConditionOperators() {
            // temporarily hard-coded index, until we fully support "and" + "or"
            // see FlexEvent in data-models.js for more info
            return this.flexEventNew.conditions[0].every(c => !!c.operator);
        },

        hasValidConditionProperties() {
            // temporarily hard-coded index, until we fully support "and" + "or"
            // see FlexEvent in data-models.js for more info
            return this.flexEventNew.conditions[0].every(c => !!c.targetProperty);
        },

        hasValidConditionValues() {
            // temporarily hard-coded index, until we fully support "and" + "or"
            // see FlexEvent in data-models.js for more info
            return this.flexEventNew.conditions[0].every((c) => {
                const operatorInstance = conditionOperators.get(c.operator);

                // no component = no targetValue needed
                return !operatorInstance.component || !!c.targetValue;
            });
        },

        reset() {
            this.flexEventNew = new FlexEvent();
            this.touched = false;
        },
    },
};
</script>

<style lang="scss" scoped>
.condition {
    &:not(:last-of-type) {
        margin-bottom: 1em;
    }

    &__label {
        min-width: 8.5em;
        text-align: right;
    }

    &__delete {
        color: $attention-color-light;
        align-self: center;
        margin-left: 0.25em;
        padding: 0.25em;
        font-size: 1.125rem;

        &:hover {
            cursor: pointer;
        }
    }
}
</style>
